/**
 * InstallPWAPrompt — subtle "Install AdmissionCrafts" pill that appears on
 * eligible mobile browsers. Listens to Chrome/Edge's `beforeinstallprompt`
 * event and, when the user taps the pill, calls `prompt()` on the deferred
 * event. On iOS Safari (which doesn't emit that event) it renders a
 * one-line hint the first time — "Tap Share → Add to Home Screen".
 *
 * The pill:
 *   - Never shows if the app is already running standalone (installed).
 *   - Only re-suppresses for {DISMISS_TTL_DAYS} after a dismiss, so if a
 *     user uninstalls the app or dismisses accidentally, the prompt
 *     naturally comes back later.
 *   - Waits 5 seconds after mount before showing, so it doesn't clash
 *     with the auth modal or any other first-visit prompt.
 */
import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "ac_pwa_dismissed_at";     // ISO timestamp of last dismiss
const IOS_HINT_KEY = "ac_pwa_ios_hint_at";     // ISO timestamp of last iOS hint dismiss
const DISMISS_TTL_DAYS = 30;

function _wasDismissedRecently(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    // Back-compat: old "1" flag → treat as recently dismissed & migrate to
    // a timestamp so it expires naturally.
    if (raw === "1") {
      localStorage.setItem(key, new Date().toISOString());
      return true;
    }
    const ts = Date.parse(raw);
    if (Number.isNaN(ts)) return false;
    const ageDays = (Date.now() - ts) / 86_400_000;
    return ageDays < DISMISS_TTL_DAYS;
  } catch (_) {
    return false;
  }
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iosDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOs = ua.includes("Mac") && "ontouchend" in document;
  return iosDevice || iPadOs;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy
    window.navigator?.standalone === true
  );
}

export default function InstallPWAPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [flavour, setFlavour] = useState("chrome"); // 'chrome' | 'ios'

  useEffect(() => {
    if (isStandalone()) return;
    if (_wasDismissedRecently(DISMISS_KEY)) return;

    // Chrome / Edge / Samsung Internet path
    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setFlavour("chrome");
      // 5s grace so it doesn't clash with the auth modal.
      setTimeout(() => setVisible(true), 5000);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari path — no BIP event exists.
    if (isIos()) {
      if (_wasDismissedRecently(IOS_HINT_KEY)) return;
      setFlavour("ios");
      setTimeout(() => setVisible(true), 6000);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  if (!visible) return null;

  const dismiss = (persistKey = DISMISS_KEY) => {
    setVisible(false);
    try {
      localStorage.setItem(persistKey, new Date().toISOString());
    } catch (_) { /* noop */ }
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      // Regardless of accept/dismiss, don't ask again this session-generation.
      dismiss(DISMISS_KEY);
      // Optional analytics could hook here: choice.outcome === 'accepted'
    } catch (_) {
      dismiss(DISMISS_KEY);
    }
  };

  if (flavour === "ios") {
    return (
      <div
        className="fixed z-40 rounded-2xl border border-slate-300 bg-white shadow-lg px-4 py-3 flex items-start gap-3 left-1/2 -translate-x-1/2 max-w-[92vw] sm:max-w-md"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)" }}
        data-testid="pwa-install-prompt-ios"
      >
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-4 h-4 text-slate-700" />
        </div>
        <div className="flex-1 min-w-0 pr-2">
          <p className="text-sm font-semibold text-slate-900">Add to Home Screen</p>
          <p className="text-xs text-slate-600 mt-0.5 leading-snug">
            Tap <span className="font-semibold">Share</span> then{" "}
            <span className="font-semibold">Add to Home Screen</span> to install.
          </p>
        </div>
        <button
          type="button"
          onClick={() => dismiss(IOS_HINT_KEY)}
          className="w-7 h-7 -mr-1 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0"
          aria-label="Dismiss"
          data-testid="pwa-install-dismiss-ios"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed z-40 rounded-2xl border border-slate-300 bg-white shadow-lg px-4 py-3 flex items-center gap-3 left-1/2 -translate-x-1/2 max-w-[92vw] sm:max-w-md"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)" }}
      data-testid="pwa-install-prompt"
    >
      <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
        <Download className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">Install AdmissionCrafts</p>
        <p className="text-xs text-slate-600 mt-0.5 leading-snug">
          Add to your home screen for faster access &amp; offline predictions.
        </p>
      </div>
      <button
        type="button"
        onClick={install}
        className="h-8 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex-shrink-0"
        data-testid="pwa-install-cta"
      >
        Install
      </button>
      <button
        type="button"
        onClick={() => dismiss(DISMISS_KEY)}
        className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
